import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dns as DnsIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { agentsAPI } from '../services/api';
import { clearCache } from '../store/dataSlice';
import { getSelectedAgent, setSelectedAgent } from '../services/api';

/**
 * Which domain controller the whole app is currently talking to.
 *
 * Selecting an agent is what TRIGGERS loading: the selection is sent on every
 * request as `X-Samba-Agent`, and changing it clears the cached directory data
 * so the next page render fetches from the newly chosen DC. Without the clear,
 * you would pick a different DC and keep looking at the previous one's users
 * for up to the 5-minute cache window -- which is worse than showing nothing.
 */
function AgentSelector() {
  const dispatch = useDispatch();
  const [agents, setAgents] = useState([]);
  const [current, setCurrent] = useState(getSelectedAgent() || '');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await agentsAPI.list();
      const list = res.data.agents || [];
      setAgents(list);

      // Settle on a sensible selection: keep the operator's choice if it is
      // still enrolled, else the default agent, else the first live one.
      const stored = getSelectedAgent();
      const stillThere = list.find((a) => a.name === stored);
      if (!stillThere) {
        const fallback =
          list.find((a) => a.is_default && a.is_live) ||
          list.find((a) => a.is_live) ||
          list[0];
        if (fallback) {
          setSelectedAgent(fallback.name);
          setCurrent(fallback.name);
        } else {
          setSelectedAgent(null);
          setCurrent('');
        }
      }
    } catch {
      // A failure here must not break the chrome; the page will surface it.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Re-check periodically so an agent going offline becomes visible without
    // a reload. Stops while the tab is hidden.
    const tick = () => {
      if (!document.hidden) load();
    };
    const id = setInterval(tick, 30000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const change = (e) => {
    const name = e.target.value;
    setSelectedAgent(name);
    setCurrent(name);
    // The whole point: switching DC invalidates everything that was read from
    // the previous one.
    dispatch(clearCache());
    // Let any mounted page know it should re-fetch immediately.
    window.dispatchEvent(new CustomEvent('samba-agent-changed', { detail: { name } }));
  };

  if (loading) return <CircularProgress size={18} sx={{ color: 'inherit', mx: 1 }} />;

  if (agents.length === 0) {
    return (
      <Tooltip title="No domain controller is enrolled. Enroll one on the Agents page.">
        <Chip size="small" color="warning" label="no agent" sx={{ mr: 1 }} />
      </Tooltip>
    );
  }

  const selected = agents.find((a) => a.name === current);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, minWidth: 0 }}>
      <DnsIcon fontSize="small" sx={{ opacity: 0.9, display: { xs: 'none', sm: 'block' } }} />
      <Typography
        variant="caption"
        sx={{ display: { xs: 'none', md: 'block' }, opacity: 0.85, letterSpacing: 0.5 }}
      >
        DOMAIN
      </Typography>
      <Select
        value={current}
        onChange={change}
        size="small"
        variant="standard"
        disableUnderline
        aria-label="Domain controller"
        sx={(theme) => {
          // This control sits on the app bar and was a bare `standard` Select
          // with no underline and `color: inherit` -- no border, no background,
          // the same colour as the bar, so there was nothing to show it WAS a
          // control. It now has its own surface, a border, and visible hover
          // and keyboard-focus states.
          const onDark = theme.palette.mode === 'dark'
            || theme.palette.primary.main !== theme.palette.background.paper;
          const tint = onDark ? theme.palette.common.white : theme.palette.common.black;
          return {
            color: 'inherit',
            minWidth: { xs: 112, sm: 190 },
            maxWidth: { xs: 170, sm: 300 },
            px: 1,
            borderRadius: 2,
            bgcolor: alpha(tint, onDark ? 0.16 : 0.06),
            border: `1px solid ${alpha(tint, onDark ? 0.45 : 0.25)}`,
            transition: theme.transitions.create(['background-color', 'border-color']),
            '&:hover': {
              bgcolor: alpha(tint, onDark ? 0.26 : 0.12),
              borderColor: alpha(tint, onDark ? 0.7 : 0.4),
            },
            '&.Mui-focused, &:focus-within': {
              bgcolor: alpha(tint, onDark ? 0.3 : 0.14),
              borderColor: alpha(tint, 0.9),
              outline: `2px solid ${alpha(tint, 0.6)}`,
              outlineOffset: 1,
            },
            '& .MuiSelect-icon': { color: 'inherit' },
            '& .MuiSelect-select': { py: 0.5, pr: '28px !important' },
            '& .MuiSelect-select:focus': { bgcolor: 'transparent' },
          };
        }}
        renderValue={(v) => {
          const a = agents.find((x) => x.name === v);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: a?.is_live ? 'success.main' : 'warning.main',
                }}
              />
              <Typography variant="body2" noWrap>
                {v || 'select a DC'}
              </Typography>
            </Box>
          );
        }}
      >
        {agents.map((a) => (
          <MenuItem key={a.name} value={a.name} disabled={!a.is_live}>
            <ListItemText
              primary={a.name}
              secondary={
                a.is_live
                  ? `${a.realm || a.hostname || 'online'}`
                  : a.enrollment_pending
                  ? 'awaiting enrollment'
                  : 'offline — cannot be selected'
              }
            />
          </MenuItem>
        ))}
      </Select>
      {selected && !selected.is_live && (
        <Chip size="small" color="warning" label="offline" />
      )}
    </Box>
  );
}

export default AgentSelector;
